/**
 * DISCOM Analyser — State Agency role screen (Gujarat only)
 * FR-S08 to FR-S10
 */
import React, { useMemo, useState } from 'react';
import { ChevronRight, Download } from 'lucide-react';
import {
  gujaratStateDiscoms,
  gujaratAvgSpecificYield,
  circleMonitoringData,
} from '../../data/mockData';

type StateDiscomRow = typeof gujaratStateDiscoms[0];

const prCls = (pr: number) =>
  pr >= 75 ? 'text-green-600 font-semibold' :
  pr >= 65 ? 'text-amber-600 font-semibold' :
             'text-red-600 font-semibold';

// ── Triage tiles ─────────────────────────────────────────────────────────────
interface TileDef {
  label: string;
  textCls: string;
  bgCls: string;
  ringCls: string;
  filterFn: (d: StateDiscomRow) => boolean;
}

const TILES: TileDef[] = [
  {
    label: 'PR below 70%',
    textCls: 'text-red-600', bgCls: 'bg-red-50', ringCls: 'ring-red-500',
    filterFn: d => d.prPercent < 70,
  },
  {
    label: 'Low specific yield',
    textCls: 'text-teal-600', bgCls: 'bg-teal-50', ringCls: 'ring-teal-500',
    filterFn: d => d.specificYield < gujaratAvgSpecificYield,
  },
  {
    label: 'Generation dropping MoM',
    textCls: 'text-amber-600', bgCls: 'bg-amber-50', ringCls: 'ring-amber-500',
    filterFn: d => d.momTrend < 0,
  },
  {
    label: 'Low availability',
    textCls: 'text-green-700', bgCls: 'bg-green-50', ringCls: 'ring-green-500',
    filterFn: d => d.availPercent < 80,
  },
  {
    label: 'High offline rate',
    textCls: 'text-orange-600', bgCls: 'bg-orange-50', ringCls: 'ring-orange-500',
    filterFn: d => (1 - d.availPercent / 100) > 0.05,
  },
  {
    label: 'High alarm density',
    textCls: 'text-purple-600', bgCls: 'bg-purple-50', ringCls: 'ring-purple-500',
    filterFn: d => d.alarms / d.plants > 0.02,
  },
];

const tileCounts = TILES.map(t => gujaratStateDiscoms.filter(t.filterFn).length);

// Drill-down levels: discom → circle → division → sub-division
type DrillLevel = 'discom' | 'circle' | 'division' | 'subdivision';

interface DrillState {
  level: DrillLevel;
  discom: string | null;
  circle: string | null;
  division: string | null;
}

const INITIAL_DRILL: DrillState = { level: 'discom', discom: null, circle: null, division: null };

export default function StateDiscomAnalyser() {
  const [activeTile, setActiveTile] = useState<number | null>(null);
  const [drill, setDrill] = useState<DrillState>(INITIAL_DRILL);

  const visibleDISCOMs = useMemo(() => {
    if (activeTile === null) return gujaratStateDiscoms;
    return gujaratStateDiscoms.filter(TILES[activeTile].filterFn);
  }, [activeTile]);

  const circleRows = useMemo(() => {
    if (!drill.discom) return [];
    return circleMonitoringData.filter(c => c.discom === drill.discom);
  }, [drill.discom]);

  const exportCSV = () => {
    let rows: string[];
    if (drill.level === 'discom') {
      rows = [
        'DISCOM,Plants,GWp,GWh Today,Sp.Yield,PR%,Avail%,CUF%,MoM%,Alarms',
        ...visibleDISCOMs.map(d =>
          `${d.name},${d.plants},${d.capacityGWp},${d.generationGWh},${d.specificYield},${d.prPercent},${d.availPercent},${d.cufPercent},${d.momTrend},${d.alarms}`)
      ];
    } else {
      rows = [
        'Circle,DISCOM,Data Tx%,Plants Offline,Active Alarms,Certs Expiring,Firmware Outdated',
        ...circleRows.map(c =>
          `${c.circle},${c.discom},${c.dataTxPercent},${c.plantsOffline},${c.activeAlarms},${c.certsExpiring},${c.firmwareOutdated}`)
      ];
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: 'discom-analyser.csv' });
    a.click();
    URL.revokeObjectURL(url);
  };

  const drillInto = (discomName: string) => {
    setDrill({ level: 'circle', discom: discomName, circle: null, division: null });
    setActiveTile(null);
  };

  const reset = () => { setDrill(INITIAL_DRILL); setActiveTile(null); };

  return (
    <div className="p-6 space-y-5">

      {/* ── Triage tiles (6) ── */}
      <div className="grid grid-cols-6 gap-3">
        {TILES.map((t, i) => {
          const isActive = activeTile === i;
          return (
            <div
              key={t.label}
              onClick={() => { setActiveTile(prev => prev === i ? null : i); reset(); }}
              className={`rounded-card border p-4 cursor-pointer transition-all hover:shadow-md ${t.bgCls}
                ${isActive ? `ring-2 ${t.ringCls} border-transparent` : 'border-border shadow-card'}`}
            >
              <p className="text-xs text-text-secondary font-semibold leading-tight">{t.label}</p>
              <p className={`text-2xl font-bold mt-1 ${t.textCls}`}>{tileCounts[i]}</p>
              <p className="text-xs text-text-tertiary mt-1">DISCOMs</p>
            </div>
          );
        })}
      </div>

      {/* ── Filter bar ── */}
      <div className="flex items-center gap-3">
        <select
          value={drill.discom ?? ''}
          onChange={e => {
            const v = e.target.value;
            if (v) drillInto(v);
            else reset();
          }}
          className="text-xs border border-border rounded-input px-2.5 py-1.5 bg-white focus:outline-none focus:border-accent"
        >
          <option value="">All DISCOMs</option>
          {gujaratStateDiscoms.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
        </select>
        {activeTile !== null && (
          <button
            onClick={() => setActiveTile(null)}
            className="text-xs text-accent hover:underline px-2 py-1 border border-accent/30 rounded-input"
          >
            Clear filter
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={exportCSV}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border border-border bg-white text-text-secondary hover:text-accent hover:border-accent transition-colors"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* ── Breadcrumb ── */}
      {drill.level !== 'discom' && (
        <div className="flex items-center gap-1 text-xs text-text-secondary">
          <button onClick={reset} className="text-accent hover:underline font-medium">All DISCOMs</button>
          <ChevronRight size={11} className="text-text-tertiary" />
          <span className="font-medium text-text-primary">{drill.discom}</span>
          {drill.circle && (
            <>
              <ChevronRight size={11} className="text-text-tertiary" />
              <span className="font-medium text-text-primary">{drill.circle}</span>
            </>
          )}
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-white rounded-card border border-border shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <p className="text-xs font-semibold text-text-primary">
            {drill.level === 'discom'
              ? `${visibleDISCOMs.length} DISCOM${visibleDISCOMs.length !== 1 ? 's' : ''}${activeTile !== null ? ` · ${TILES[activeTile].label}` : ''}`
              : `Circles — ${drill.discom}`
            }
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary border-b border-border">
              <tr>
                {drill.level === 'discom' ? (
                  <>
                    <th className="text-left py-2.5 px-4 text-text-secondary font-semibold">Name</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Plants</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Capacity</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Gen today</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Sp. yield</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">PR %</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Avail %</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">CUF %</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">MoM %</th>
                    <th className="text-left py-2.5 px-3 text-text-secondary font-semibold">Active / Offline</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Alarms</th>
                  </>
                ) : (
                  <>
                    <th className="text-left py-2.5 px-4 text-text-secondary font-semibold">Circle</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Data Tx %</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Plants Offline</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Active Alarms</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Certs Expiring</th>
                    <th className="text-right py-2.5 px-3 text-text-secondary font-semibold">Firmware Outdated</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {drill.level === 'discom' ? (
                visibleDISCOMs.map(d => {
                  const activePl  = Math.round(d.plants * d.availPercent / 100);
                  const offlinePl = d.plants - activePl;
                  return (
                    <tr key={d.id} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                      <td className="py-2.5 px-4">
                        <button
                          onClick={() => drillInto(d.name)}
                          className="font-medium text-accent hover:underline text-left"
                        >
                          {d.name}
                        </button>
                      </td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.plants.toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.capacityGWp.toFixed(2)} GWp</td>
                      <td className="py-2.5 px-3 text-right font-medium text-text-primary">{d.generationGWh.toFixed(2)} GWh</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.specificYield.toFixed(2)} kWh/kWp</td>
                      <td className={`py-2.5 px-3 text-right ${prCls(d.prPercent)}`}>{d.prPercent.toFixed(1)}%</td>
                      <td className={`py-2.5 px-3 text-right ${d.availPercent < 80 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{d.availPercent.toFixed(0)}%</td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">{d.cufPercent.toFixed(1)}%</td>
                      <td className={`py-2.5 px-3 text-right font-semibold ${d.momTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {d.momTrend >= 0 ? '↑' : '↓'}{Math.abs(d.momTrend).toFixed(1)}%
                      </td>
                      <td className="py-2.5 px-3 text-xs text-text-secondary">
                        {activePl.toLocaleString()} active · <span className={offlinePl > d.plants * 0.05 ? 'text-red-600 font-semibold' : ''}>{offlinePl.toLocaleString()} offline</span>
                      </td>
                      <td className={`py-2.5 px-3 text-right ${d.alarms > 40 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{d.alarms}</td>
                    </tr>
                  );
                })
              ) : (
                circleRows.length > 0 ? circleRows.map(c => (
                  <tr key={c.circle} className="border-b border-border/50 hover:bg-bg-secondary transition-colors">
                    <td className="py-2.5 px-4 font-medium text-text-primary">{c.circle}</td>
                    <td className={`py-2.5 px-3 text-right ${c.dataTxPercent < 90 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{c.dataTxPercent.toFixed(1)}%</td>
                    <td className={`py-2.5 px-3 text-right ${c.plantsOffline > 0 ? 'text-red-600 font-semibold' : 'text-text-secondary'}`}>{c.plantsOffline}</td>
                    <td className={`py-2.5 px-3 text-right ${c.activeAlarms > 5 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{c.activeAlarms}</td>
                    <td className={`py-2.5 px-3 text-right ${c.certsExpiring > 0 ? 'text-amber-600 font-semibold' : 'text-text-secondary'}`}>{c.certsExpiring}</td>
                    <td className={`py-2.5 px-3 text-right ${c.firmwareOutdated > 0 ? 'text-text-secondary' : 'text-green-600'}`}>{c.firmwareOutdated}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-xs text-text-tertiary">
                      No circle data available for {drill.discom}.
                    </td>
                  </tr>
                )
              )}
              {drill.level === 'discom' && visibleDISCOMs.length === 0 && (
                <tr>
                  <td colSpan={11} className="py-10 text-center text-xs text-text-tertiary">
                    No DISCOMs match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
